import { useContext, useEffect, useRef } from 'react';
import type { Audio as ECAudio, AudioContextType } from '../../../dorian-shared/types/audio/audio';
import { CursorPosition } from '../../../contexts/Cursor/cursorReducer';
import { NodeGetter } from './getNode';
import { createStepAudioPlayback, StepAudioPlayback } from './stepAudioPlayback';
import { processAudios, stopAllAudio } from '../../pages/AudioStepSettingsModal/AudioPlay/AudioController';
import { FEATURE_NODE_EDIT_SIDE_PANEL } from '../../../helpers/featureFlags';
import { resolveStepAudios } from './resolveStepAudios';
import { setStoredStepAudios } from './useChoiceMemoryCallbacks/useChoiceMemoryCallbacks';
import { AudiosContext } from '../../pages/AudioLibraryPage/AudioLibraryContainer/AudiosProvider/AudiosProvider';

function collectStepAudioIds(stepAudios: unknown): Array<string | number> {
  if (!Array.isArray(stepAudios)) return [];
  return stepAudios
    .map((entry) => (entry as { audioId?: string | number | null })?.audioId)
    .filter((id): id is string | number => id != null);
}

// getAllV3 cache omits Audio sub-object — resolveStepAudios hydrates it before delivery.
export function useStepAudioSubscriber(
  cursor: CursorPosition | null,
  nodeGetter: NodeGetter,
  audios: ECAudio[],
  audioPath: string | null,
): void {
  const playbackRef = useRef<StepAudioPlayback | null>(null);
  const { ensureAudios, pinAudioIds } = (useContext(AudiosContext) as AudioContextType | undefined) ?? {};

  if (playbackRef.current === null) {
    playbackRef.current = createStepAudioPlayback({ processAudios, stopAllAudio });
  }

  useEffect(() => {
    if (!FEATURE_NODE_EDIT_SIDE_PANEL) {
      return;
    }
    if (!cursor) {
      pinAudioIds?.('preview', []);
      return;
    }
    const node = nodeGetter(cursor.branchId);
    const step = cursor.stepIndex != null ? node?.steps?.[cursor.stepIndex] : undefined;
    ensureAudios?.(collectStepAudioIds(step?.stepAudios), 'preview');
  }, [cursor, nodeGetter, ensureAudios, pinAudioIds]);

  useEffect(() => {
    if (!cursor) {
      return;
    }
    if (!FEATURE_NODE_EDIT_SIDE_PANEL) {
      return;
    }

    const node = nodeGetter(cursor.branchId);
    const step = cursor.stepIndex != null ? node?.steps?.[cursor.stepIndex] : undefined;

    const resolvedAudios = resolveStepAudios(step?.stepAudios, audios, audioPath);

    if (playbackRef.current) {
      playbackRef.current.deliver(
        { branchId: cursor.branchId, stepIndex: cursor.stepIndex },
        resolvedAudios ?? undefined,
      );
    }

    // Null on empty step prevents a stale previous node from replaying after unmute.
    setStoredStepAudios(resolvedAudios && resolvedAudios.length > 0 ? resolvedAudios : null);
  // playbackRef.current is mutated in place — adding it to deps would cause spurious re-runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, nodeGetter, audios, audioPath]);
}
